import { Inject, Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { ReplaySubject, lastValueFrom, switchMap, timer } from 'rxjs';
import { AppConfig } from '../../app.config';
import { WINDOW } from '../../inject-globals';
import { visibilityStateOnce$ } from '../../utilities/page-visibility';

declare global {
  interface IonicDeeplink {
    onDeepLink: (cb: (data: IonicDeepLinkData) => void) => void;
  }

  interface IonicDeepLinkData {
    url: string;
    host: string;
    path: string;
    scheme: string;
    fragment?: string;
    queryString?: string;
    extra?: Record<string, any>;
  }
  interface Window {
    IonicDeeplink: IonicDeeplink;
  }
}

export interface DeepLinkData extends IonicDeepLinkData {
  fullPath: string;
}

@Injectable({ providedIn: 'root' })
export class DeepLink {
  data$ = new ReplaySubject<DeepLinkData>(1);

  constructor(private platform: Platform, @Inject(AppConfig) private config: AppConfig, @Inject(WINDOW) private window: Window) {
    this.init();
  }

  async init() {
    /* istanbul ignore if reason: not necessary */
    if (!this.platform.is('cordova')) return;
    await this.platform.ready();
    window.IonicDeeplink.onDeepLink(data => {
      if (data) {
        // remove scheme/protocol
        let fullPath = data.url.split('://')[1];
        // when http scheme, remove domain
        if (data.scheme.includes('http')) {
          const paths = fullPath.split('/');
          paths.shift();
          fullPath = paths.join('/');
        }
        this.data$.next({ ...data, fullPath: '/' + fullPath });
      }
    });
  }

  async openApp() {
    const url = this.config.SCHEME_URL;

    if (this.platform.is('ios')) this.navigateToIOSApp(url);
    else if (this.platform.is('android')) this.navigateToAndroidApp(url);
  }

  private navigateToIOSApp(url: string) {
    setTimeout(() => {
      // redirect to app store ignores iOS error when app is not installed
      // it will redirect to store even app is installed but wait for alert to be dismissed
      this.window.location.href = this.config.APP_STORE_REDIRECT;
    }, 50);

    this.window.location.href = url;
  }

  private async navigateToAndroidApp(url: string) {
    this.window.location.href = url;
    const state = await lastValueFrom(timer(300).pipe(switchMap(() => visibilityStateOnce$)));

    if (state !== 'blur') {
      this.window.location.href = this.config.PLAY_STORE_REDIRECT;
    }
  }
}
